import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { onBrandSearch } from "../../controller/BrandController";
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, InputGroup, Table, Pagination, Spinner, Badge } from 'react-bootstrap';
import { Page } from "../../dto/page/Page";
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import { BrandSearch } from "../../dto/brand/BrandSearch";
import { BrandListEntity } from "../../dto/brand/BrandListEntity";
import { BrandEntity } from "../../dto/brand/BrandEntity";
import { getBrandStatus } from "../../app/AppConst";

const BrandList = () => {
    const navigate = useNavigate();

    // ---- Auth check
    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        }
    }, []);

    const [brands, setBrands] = useState<BrandListEntity>({
        data: []
    });

    const [search, setSearch] = useState<BrandSearch>({
        limit: 10
    });

    // ---- Load brands
    const loadBrands = async (page: number = 0) => {
        const params: BrandSearch = {
            ...search,
            offset: page
        };

        const response = await onBrandSearch(params);

        if (response.status !== 0) {
            toast.error("Брэнд уншихад алдаа гарлаа");
            return;
        }

        const pageable = response.data;

        const pageInfo: Page = {
            limit: search.limit,
            totalElements: pageable.totalElements,
            totalPages: pageable.totalPages,
            offset: page
        };

        const beans: Array<BrandEntity> = pageable.beans;

        setBrands({
            page: pageInfo,
            data: beans
        });

        setSearch(prev => ({ ...prev, offset: page }));
    };

    // ---- Search submit
    const brandSearchSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        loadBrands(0); // reset to first page on new search
    };

    // ---- Pagination click
    const changePage = (page: number) => {
        if (!brands.page) return;
        if (page < 0 || page >= brands.page.totalPages) return;
        loadBrands(page);
    };

    return (
        <Container fluid>
            <Toaster position="top-right" />

            <h3 className="mb-3">
                <a onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                    <big>&#8592;</big>
                </a> Брэнд
            </h3>

            {/* Search Card */}
            <Row className="mb-3">
                <Col md={12}>
                    <Card>
                        <Card.Header>
                            <Form onSubmit={brandSearchSubmit}>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label className="text-center">
                                                Нэр
                                            </Form.Label>
                                            <Form.Control size="sm" type="text" 
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                if (value !== "") {
                                                    setSearch({
                                                        ...search,
                                                        name: event.target.value
                                                    })
                                                } else {
                                                    const { name, ...rest } = search;
                                                    setSearch(rest);
                                                }
                                            }} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Багцлах</Form.Label>
                                            <InputGroup>
                                                <Form.Select
                                                    size="sm"
                                                    value={search.limit}
                                                    onChange={(e) =>
                                                        setSearch({
                                                            ...search,
                                                            limit: parseInt(e.target.value),
                                                        })
                                                    }
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="0">Бүгд</option>
                                                </Form.Select>
                                                <Button variant="primary" size="sm" type="submit">
                                                    <SearchIcon fontSize="small" />
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Header>

                        {/* Table */}
                        <Card.Body>
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Sort Order</th>
                                        <th>Status</th>
                                        <th style={{ width: 80 }}>Info</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {brands.data.length === 0 && (
                                        <tr>
                                            <td className="text-muted">
                                                Илэрц олдсонгүй
                                            </td>
                                        </tr>
                                    )}

                                    {brands.data.map((data) => (
                                        <tr key={data.id}>
                                            <td>{data.name}</td>
                                            <td>{data.sortOrder}</td>
                                            <td>
                                                <Badge bg={getBrandStatus(data.status)}>{data.status}</Badge>
                                            </td>
                                            <td>
                                                <a href={`/brand/${data.id}`}>
                                                    <InfoIcon />
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {brands.page && brands.page.totalPages > 1 && (() => {
                const page = brands.page;   // now TypeScript knows it's NOT undefined

                return (
                    <Row>
                        <Col className="d-flex justify-content-start">
                            <Pagination>
                                <Pagination.First
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(0)}
                                />

                                <Pagination.Prev
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(page.offset - 1)}
                                />

                                {Array.from({ length: page.totalPages })
                                    .slice(
                                        Math.max(page.offset - 2, 0),
                                        Math.min(page.offset + 3, page.totalPages)
                                    )
                                    .map((_, idx) => {
                                        const pageNumber =
                                            Math.max(page.offset - 2, 0) + idx;

                                        return (
                                            <Pagination.Item
                                                key={pageNumber}
                                                active={pageNumber === page.offset}
                                                onClick={() => changePage(pageNumber)}
                                            >
                                                {pageNumber + 1}
                                            </Pagination.Item>
                                        );
                                    })}

                                <Pagination.Next
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.offset + 1)}
                                />

                                <Pagination.Last
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.totalPages - 1)}
                                />
                            </Pagination>
                        </Col>
                    </Row>
                );
            })()}
        </Container>
    );
};

export default BrandList;
